/**
 * P3-23/24: Workshop tab 按钮模型 hooks。
 *
 * - CraftButton：走 `CraftButtonController`（ButtonModernController 子类），
 *   每 tick fetchModel 快照订阅，同时从 modelRef 读取工程师分配链接。
 * - UpgradeButton / ZebraUpgradeButton：复用 Science tab 的同构
 *   `useScienceButton`（同属 BuildingNotStackableBtnController 族）。
 */
import { useCallback, useMemo } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import {
  useButtonModel,
  type ModernButtonController,
} from "../bonfire/useButtonModel";
import { useScienceButton } from "../science/useScienceModel";

// ---------------------------------------------------------------------------
// Craft
// ---------------------------------------------------------------------------

export interface CraftLinkInfo {
  id?: string;
  title: string;
}

export interface CraftButtonSnapshot {
  name: string;
  description: string;
  visible: boolean;
  enabled: boolean;
  resourceIsLimited: string | boolean;
  /** craftOn + bldEnabled/bldlackResConvert 目标 class */
  domClassName: string;
  unassignLinks: CraftLinkInfo[];
  assignLinks: CraftLinkInfo[];
}

interface CraftLike {
  name: string;
  label: string;
  description: string;
  unlocked: boolean;
  value: number;
  isLimitedAmt?: boolean;
}

function toCraftSnapshot(model: Record<string, unknown>): CraftButtonSnapshot {
  const craft = (model.craft ?? {}) as CraftLike;
  const val = craft.value ?? 0;
  let domClassName = "";
  if (val > 0) {
    domClassName += " craftOn";
    domClassName += craft.isLimitedAmt ? " bldlackResConvert" : " bldEnabled";
  }
  const unassign = ((model.unassignCraftLinks as CraftLinkInfo[] | undefined) ?? []).map(
    (l) => ({ id: l.id, title: l.title }),
  );
  const assign = ((model.assignCraftLinks as CraftLinkInfo[] | undefined) ?? []).map((l) =>
    ({ id: l.id, title: l.title }),
  );
  return {
    name: String(model.name ?? ""),
    description: String(model.description ?? ""),
    visible: !!model.visible,
    enabled: !!model.enabled,
    resourceIsLimited: (model.resourceIsLimited as string | boolean) ?? "",
    domClassName,
    unassignLinks: unassign,
    assignLinks: assign,
  };
}

function craftSnapshotEq(
  a: CraftButtonSnapshot | null,
  b: CraftButtonSnapshot | null,
): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return (
    a.name === b.name &&
    a.description === b.description &&
    a.visible === b.visible &&
    a.enabled === b.enabled &&
    a.resourceIsLimited === b.resourceIsLimited &&
    a.domClassName === b.domClassName &&
    a.unassignLinks.length === b.unassignLinks.length &&
    a.assignLinks.length === b.assignLinks.length &&
    a.unassignLinks.every(
      (l, i) => l.title === b.unassignLinks[i].title && l.id === b.unassignLinks[i].id,
    ) &&
    a.assignLinks.every(
      (l, i) => l.title === b.assignLinks[i].title && l.id === b.assignLinks[i].id,
    )
  );
}

type ControllerCtor = new (game: GamePage) => ModernButtonController;

function getController(
  getter: () => ControllerCtor | undefined,
): ModernButtonController | null {
  const game = getGame();
  const Ctor = getter();
  if (!Ctor || !game) {
    return null;
  }
  return new Ctor(game);
}

function getCraftControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    com?: {
      nuclearunicorn?: {
        game?: {
          ui?: { CraftButtonController?: ControllerCtor };
        };
      };
    };
  };
  return w.com?.nuclearunicorn?.game?.ui?.CraftButtonController;
}

export interface CraftButtonModelResult {
  snap: CraftButtonSnapshot | null;
  modelRef: React.MutableRefObject<Record<string, unknown> | null>;
  controller: ModernButtonController | null;
  getTooltip: () => string;
}

export function useCraftButtonModel(
  craftName: string,
): CraftButtonModelResult {
  const controller = useMemo(
    () => getController(getCraftControllerCtor),
    [],
  );

  const createOpts = useCallback(
    (game: GamePage) => {
      const workshop = game.workshop as {
        getCraft(name: string): CraftLike;
        getCraftPrice(craft: CraftLike): { name: string; val: number }[];
      };
      const craft = workshop.getCraft(craftName);
      return {
        name: craft.label,
        description: craft.description,
        craft: craftName,
        prices: workshop.getCraftPrice(craft),
      };
    },
    [craftName],
  );

  const { snap, modelRef } = useButtonModel(
    controller,
    createOpts,
    toCraftSnapshot,
    craftSnapshotEq,
  );

  const getTooltip = useCallback(() => {
    const g = getGame();
    const helper = window.ButtonModernHelper;
    if (!g || !controller || !helper) {
      return "";
    }
    return helper.getTooltipHTML(controller, { options: createOpts(g) });
  }, [controller, createOpts]);

  return { snap, modelRef, controller, getTooltip };
}

// ---------------------------------------------------------------------------
// Upgrade
// ---------------------------------------------------------------------------

function getUpgradeControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    com?: {
      nuclearunicorn?: {
        game?: {
          ui?: { UpgradeButtonController?: ControllerCtor };
        };
      };
    };
  };
  return w.com?.nuclearunicorn?.game?.ui?.UpgradeButtonController;
}

export function useUpgradeButtonModel(upgradeName: string) {
  return useScienceButton(upgradeName, getUpgradeControllerCtor);
}

// ---------------------------------------------------------------------------
// Zebra upgrade
// ---------------------------------------------------------------------------

function getZebraUpgradeControllerCtor(): ControllerCtor | undefined {
  const w = window as unknown as {
    com?: {
      nuclearunicorn?: {
        game?: {
          ui?: { ZebraUpgradeButtonController?: ControllerCtor };
        };
      };
    };
  };
  return w.com?.nuclearunicorn?.game?.ui?.ZebraUpgradeButtonController;
}

export function useZebraUpgradeButtonModel(upgradeName: string) {
  return useScienceButton(upgradeName, getZebraUpgradeControllerCtor);
}
